// inv/src/components/ui/menu.jsx
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { cn } from "../../lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";

function TabDropdown({ tab }) {
  const [open, setOpen] = React.useState(false);
  const close = () => setOpen(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger
        className={cn(
          "flex cursor-pointer select-none items-center rounded-md px-3 py-1",
          "text-sm font-medium text-stone-950 outline-none",
          "bg-transparent",
          "focus-visible:ring-2 focus-visible:ring-stone-400/70"
        )}
      >
        {tab.label}
        <span className="ml-2 inline-flex">
          {open ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </span>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={cn(
            "z-50 mt-1 min-w-[220px] rounded-lg border border-stone-200",
            "bg-white p-1.5 shadow-lg",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in data-[state=closed]:fade-out",
            "data-[side=bottom]:slide-in-from-top-1"
          )}
          side="bottom"
          sideOffset={4}
          align="start"
          avoidCollisions
          collisionPadding={10}
        >
          {(tab.items ?? []).map((item, idx) => (
            <DropdownMenu.Item
              key={idx}
              className={cn(
                "my-0.5 flex w-full items-center justify-between rounded-md px-2 py-1.5",
                "text-sm text-stone-950 outline-none",
                "cursor-pointer",
                "hover:bg-stone-100",
                "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                item.disabled && "cursor-not-allowed opacity-50",
                item.className
              )}
              disabled={item.disabled}
              onSelect={(e) => {
                if (item.disabled) {
                  e.preventDefault();
                  return;
                }
                item.onClick?.(e);
                close();
              }}
            >
              <span className="flex-grow truncate">{item.label}</span>

              {Array.isArray(item.icons) && item.icons.length > 0 && (
                <div className="ml-3 flex flex-shrink-0 items-center space-x-1.5">
                  {item.icons.map((iconItem, iconIdx) => {
                    const IconComp = iconItem.icon;
                    const isDisabled = item.disabled || iconItem.disabled;

                    return (
                      <button
                        key={iconIdx}
                        type="button"
                        disabled={isDisabled}
                        className={cn(
                          "inline-flex h-6 w-6 items-center justify-center rounded-md",
                          "cursor-pointer",
                          "hover:bg-stone-200 focus-visible:outline-none",
                          "focus-visible:ring-2 focus-visible:ring-stone-400/70",
                          "disabled:opacity-50 disabled:cursor-not-allowed"
                        )}
                        onClick={(e) => {
                          if (isDisabled) return;
                          e.stopPropagation();
                          iconItem.onClick?.(e);
                          close();
                        }}
                        title={iconItem.title}
                        aria-label={iconItem.ariaLabel || iconItem.title}
                        aria-disabled={isDisabled || undefined}
                      >
                        <IconComp className="h-4 w-4 text-stone-950" {...iconItem.iconProps} />
                      </button>
                    );
                  })}
                </div>
              )}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export const Menu = ({ tabs }) => (
  <div className={cn("flex h-8 items-center gap-1 rounded-lg border border-stone-200", "bg-white p-1 shadow-sm")}>
    {(tabs ?? []).map((tab, i) => (
      <TabDropdown key={i} tab={tab} />
    ))}
  </div>
);
